import { CommonLayout } from "../../common/components/CommonLayout";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import searchIcon from "../../assets/icons/searchIcon.svg";
import { RequestLayout } from "../../common/components/RequestLayout";
import { Field, Formik } from "formik";
import CustomInput from "../../common/components/CustomInput/CustomInput";
import * as Yup from "yup";
import { CustomMainButton } from "../../common/components/CustomMainButton";
import Appointment from "../../../Models/Appointment/Appointment";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { validatePhoneNumber } from "../../../Utils";
import { RoutePath } from "../../../Utils/routePath";
import { useNavigate } from "react-router";
import { CommonLayoutTitle } from "../../common/components/CommonLayoutTitle";
import Media from "../../../Models/Media/Media";
import { MobileTitle } from "../../common/components/MobileTitle";
import { Typography } from "@mui/material";
import { css } from "aphrodite";
import PhoneNumberInputSelect from "../../common/components/PhoneNumberInputSelect.js/PhoneNumberInputSelect";

const FindYourAppointmentPage = () => {
    const [t] = useTranslation(["translation"]);
    const navigate = useNavigate();
    const appointmentInfoData = useSelector(Appointment.getAppointmentInfoData);
    const appointmentInfoLoading = useSelector(Appointment.getAppointmentInfoLoading);
    const appointmentInfoError = useSelector(Appointment.getAppointmentInfoError);
    const [emailOrNumber, setEmailOrNumber] = useState("");
    const [searchSubmitted, setSearchSubmitted] = useState(false);
    const webView = useSelector(Media.getIsWebView);

    useEffect(() => {
        
        if (!searchSubmitted && appointmentInfoData?.id) {
            Appointment.resetState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); 

    useEffect(() => {
        if (searchSubmitted && appointmentInfoData?.id && !appointmentInfoLoading) {
            setSearchSubmitted(false);
            Appointment.setSelectedNumber(emailOrNumber);
            navigate(RoutePath.cancelAppointmentPage);
        }
    }, [searchSubmitted, appointmentInfoData, appointmentInfoLoading, emailOrNumber, navigate]);

    const handleFindAppointmentButtonClicked = (values) => {
        setSearchSubmitted(true);
        Appointment.setSelectedEmail(values.email);
        Appointment.fetchAppointmentInfo(
            values.referenceNumber, values.emailOrNumber, values.email
        );
        setEmailOrNumber(values.emailOrNumber);
    }

    const validationSchema = Yup.object().shape({
        emailOrNumber: Yup.string()
            .test(
                "email-or-phone",
                t("Must be a valid email or phone number"),
                validatePhoneNumber
            )
            .required(t("Email or phone number is required")),
        email: Yup.string()
            .email(t("Invalid email"))
            .required(t("Email is required")),
        referenceNumber: Yup.string()
            .required(t("Reference number is required")),
    });

    const handleGoBack = () => {
        navigate(-1);
    }

    return (
        webView ? (
            <CommonLayout>
                <CommonLayoutTitle
                    statusTitle= {t("Home Page")}
                    statusSubtitle= {t("Find your appointment")}
                    iconSrc={searchIcon}
                    title={t("Find your appointment")}
                    description={t(
                        "Please enter the phone you provided when you booked your appointment, as well as the Refference Number to your appointment. Your Reference Number has been sent to the e-mail and/or phone that you provided."
                    )}
                />
                <RequestLayout loading={appointmentInfoLoading} error={appointmentInfoError}>
                    <Formik
                        initialValues={{
                            emailOrNumber: "",
                            email: "",
                            referenceNumber: "",
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleFindAppointmentButtonClicked}
                    >
                        {({
                            handleSubmit,
                            handleChange,
                            handleBlur,
                            values,
                            touched,
                            isValid,
                            errors,
                            dirty,
                        }) => (
                            <>
                                <Field
                                    id="emailOrNumber"
                                    label={t("Phone")}
                                    type="text"
                                    name="emailOrNumber"
                                    placeholder={t("Phone") + " *"}
                                    value={values.emailOrNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    component={PhoneNumberInputSelect}
                                />
                                <Field
                                    id="email"
                                    label={t("E-mail")}
                                    type="text"
                                    name="email"
                                    placeholder="name@domain.com *"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    component={CustomInput}
                                />
                                <Field
                                    id="referenceNumber"
                                    label={t("Reference Number")}
                                    type="text"
                                    name="referenceNumber"
                                    placeholder={t("Reference Number") + " *"}
                                    value={values.referenceNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    component={CustomInput}
                                />
                                <CustomMainButton
                                    isDisabled={!(isValid && dirty)}
                                    fixedSize={false}
                                    value={t("Find my appointment")}
                                    textColor="inherit"
                                    style={[styles.button, !(isValid && dirty) && styles.disableStyle]}
                                    onClick={handleSubmit}
                                />
                            </>
                        )}
                    </Formik>
                </RequestLayout>
            </CommonLayout>
        ) : (
            <CommonLayout>
                <MobileTitle iconSrc={searchIcon} title={t("Find Appointment")} onClickBackButton={handleGoBack}/>
                <Typography variant="body1" >
                    {t("Please enter the phone you provided when you booked your appointment, as well as the Refference Number to your appointment. Your Reference Number has been sent to the e-mail and/or phone that you provided.")}
                </Typography>
                <RequestLayout loading={appointmentInfoLoading} error={appointmentInfoError}>
                    <Formik
                        initialValues={{
                            emailOrNumber: "",
                            email: "",
                            referenceNumber: "",
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleFindAppointmentButtonClicked}
                    >
                        {({
                            handleSubmit,
                            handleChange,
                            handleBlur,
                            values,
                            touched,
                            isValid,
                            errors,
                            dirty,
                        }) => (
                            <div className={css(styles.mobileContainer)}>
                                <Field
                                    id="emailOrNumber-mobile"
                                    label={t("Phone")}
                                    type="text"
                                    name="emailOrNumber"
                                    placeholder={t("Phone") + " *"}
                                    value={values.emailOrNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    component={PhoneNumberInputSelect}
                                />
                                <Field
                                    id="email-mobile"
                                    label={t("E-mail")}
                                    type="text"
                                    name="email"
                                    placeholder="name@domain.com *"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    component={CustomInput}
                                />
                                <Field
                                    id="referenceNumber-mobile"
                                    label={t("Reference Number")}
                                    type="text"
                                    name="referenceNumber"
                                    placeholder={t("Reference Number") + " *"}
                                    value={values.referenceNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    component={CustomInput}
                                />
                                <CustomMainButton
                                    isDisabled={!(isValid && dirty)}
                                    fixedSize={false}
                                    value={t("Find my appointment")}
                                    textColor="inherit"
                                    style={[styles.mobileButton, !(isValid && dirty) && styles.disableStyle]}
                                    onClick={handleSubmit}
                                />
                            </div>
                        )}
                    </Formik>
                </RequestLayout>
            </CommonLayout>
        )
    );
};

export default FindYourAppointmentPage;
